import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapPin, Pencil, Search, X } from "lucide-react";
import { AU_CITIES } from "@/data/aussieCities";
import { STATE_COLORS } from "@/lib/constants";
import { stateFromPostcode } from "@/data/visa88Days";

/**
 * Inline compact location picker for the periods table.
 * - When empty: shows an amber "Choose location" button that opens a searchable popover.
 * - When set: shows the city name + postcode + colored state chip, clickable to edit.
 *
 * Props:
 *   value            {string}  current postcode ("6714", "" if none)
 *   onChange         {func}    fn(newPostcodeString)
 *   labelChoose      {string}  i18n: "Choose location"
 *   labelChange      {string}  i18n: "Change location"
 *   hint             {string}  i18n: "Search a city, postcode or region"
 *   testIdPrefix     {string}  base for data-testid
 */
export function InlineLocationPicker({
  value,
  onChange,
  labelChoose = "Choose location",
  labelChange = "Change location",
  hint = "Search a city, postcode or region",
  testIdPrefix = "inline-loc",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const suggestions = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return AU_CITIES.slice(0, 8); // show a few popular defaults
    const isDigit = /^\d/.test(q);
    const starts = [];
    const contains = [];
    const postcode = [];
    const stateMatch = [];
    const qUpper = q.toUpperCase();
    for (const c of AU_CITIES) {
      const n = c.name.toLowerCase();
      if (isDigit) {
        if (c.postcode.startsWith(q)) postcode.push(c);
      } else if (c.state === qUpper || c.state.toLowerCase() === q) {
        stateMatch.push(c);
      } else if (n.startsWith(q)) {
        starts.push(c);
      } else if (n.includes(q)) {
        contains.push(c);
      }
    }
    return [...postcode, ...stateMatch, ...starts, ...contains].slice(0, 12);
  }, [query]);

  // Look up city info for display
  const cityInfo = useMemo(() => {
    const pc = String(value || "").trim();
    if (!pc) return null;
    const match = AU_CITIES.find((c) => c.postcode === pc);
    if (match) return { name: match.name, postcode: match.postcode, state: match.state };
    // Fallback: try to derive state from postcode
    const state = stateFromPostcode(parseInt(pc, 10));
    return { name: "", postcode: pc, state: state || "" };
  }, [value]);

  const pick = (city) => {
    onChange(city.postcode);
    setOpen(false);
    setQuery("");
  };

  const clearLocation = (e) => {
    e.stopPropagation();
    onChange("");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const hasValue = !!cityInfo;
  const stateColor = cityInfo ? STATE_COLORS[cityInfo.state] || "#78716C" : "#78716C";

  return (
    <div ref={wrapRef} className="relative inline-block" data-testid={testIdPrefix}>
      {hasValue ? (
        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border border-[#EAE5DB] bg-white hover:border-[#292524] transition-colors text-xs group"
          data-testid={`${testIdPrefix}-current`}
          title={labelChange}
        >
          <MapPin className="w-3 h-3 flex-shrink-0" style={{ color: stateColor }} />
          {cityInfo.name ? (
            <span className="font-semibold text-[#292524]">{cityInfo.name}</span>
          ) : null}
          {cityInfo.state && (
            <span
              className="inline-block px-1 py-0 rounded text-[9px] font-bold text-white leading-4"
              style={{ backgroundColor: stateColor }}
            >
              {cityInfo.state}
            </span>
          )}
          <span className="text-[#78716C]">{cityInfo.postcode}</span>
          <Pencil className="w-3 h-3 text-[#78716C] opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-[#E5A93D] bg-[#FDF7E6] text-[#8B6A1E] hover:bg-[#F9EDCC] transition-colors text-xs font-semibold"
          data-testid={`${testIdPrefix}-choose`}
        >
          <MapPin className="w-3 h-3" />
          {labelChoose}
        </button>
      )}

      {open && (
        <div
          className="absolute z-50 left-0 top-full mt-1.5 w-[280px] bg-white border border-[#EAE5DB] rounded-xl shadow-2xl overflow-hidden"
          data-testid={`${testIdPrefix}-menu`}
        >
          <div className="p-2 border-b border-[#F2EBE1] relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#78716C] pointer-events-none" />
            <input
              ref={inputRef}
              type="text"
              placeholder={hint}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
              onKeyDown={onKeyDown}
              className="w-full text-xs pl-7 pr-2 py-1.5 rounded-lg border border-[#EAE5DB] focus:border-[#D95D39] focus:outline-none"
              autoComplete="off"
              data-testid={`${testIdPrefix}-search`}
            />
          </div>
          <div className="max-h-[240px] overflow-auto">
            {suggestions.length === 0 && (
              <div className="px-3 py-4 text-xs text-center text-[#78716C]">
                No result
              </div>
            )}
            {suggestions.map((c, idx) => {
              const sc = STATE_COLORS[c.state] || "#57534E";
              return (
                <button
                  key={`${c.name}-${c.postcode}`}
                  type="button"
                  onMouseEnter={() => setActiveIdx(idx)}
                  onClick={() => pick(c)}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 text-xs transition-colors border-b border-[#F2EBE1] last:border-0 ${
                    idx === activeIdx ? "bg-[#FAF6F0]" : "bg-white"
                  }`}
                  data-testid={`${testIdPrefix}-option-${c.postcode}`}
                >
                  <MapPin className="w-3 h-3 flex-shrink-0" style={{ color: sc }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#292524] truncate">{c.name}</div>
                    <div className="text-[10px] text-[#78716C] flex items-center gap-1">
                      <span
                        className="inline-block px-1 py-0 rounded text-[9px] font-bold text-white leading-4"
                        style={{ backgroundColor: sc }}
                      >
                        {c.state}
                      </span>
                      {c.postcode}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          {hasValue && (
            <div className="border-t border-[#F2EBE1] p-2">
              <button
                type="button"
                onClick={clearLocation}
                className="w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-[11px] text-[#78716C] hover:text-[#D9423E] hover:bg-[#FEF2F2] rounded-lg transition-colors"
                data-testid={`${testIdPrefix}-clear`}
              >
                <X className="w-3 h-3" /> Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
